import { useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Table, Dropdown, DropdownButton, Badge } from 'react-bootstrap'
import Lottie from 'lottie-react'
import loaderIris from '../../../lotties/loader-iris.json'
import { usePriceExtractionsFetch } from '../../../hooks/fetch/usePriceExtractionsFetch'
import { PriceExtraction, ExtractionStatus } from '../../../interfaces/priceExtraction'
import Pagination from '../../../components/ui/pagination/Pagination'
import SpecialSelect, { OptionSelectType } from '../../../components/ui/SpecialSelect'

const LIMIT = 20
const MAX_PAGES = 4

// Países disponibles (hardcoded)
const COUNTRIES = [
    { id: 1, name: 'Uruguay' },
    { id: 2, name: 'Paraguay' },
    { id: 3, name: 'Bolivia' },
    { id: 4, name: 'España' },
    { id: 5, name: 'Colombia' },
    { id: 6, name: 'Panamá' },
    { id: 7, name: 'Chile' },
    { id: 8, name: 'República Dominicana' },
    { id: 9, name: 'Costa Rica' }
]

const statusLabels: Record<ExtractionStatus, string> = {
    pending: 'Pendiente',
    processing: 'Procesando',
    completed: 'Validar',
    failed: 'Fallida',
    validated: 'Validada',
    applied: 'Aplicada'
}

const statusColors: Record<ExtractionStatus, string> = {
    pending: 'secondary',
    processing: 'info',
    completed: 'primary',
    failed: 'danger',
    validated: 'warning',
    applied: 'success'
}

export const ListExtractions = () => {
    const navigate = useNavigate()
    const { getExtractions, getProjectsWithExtractions, getCreators, showAlertMessage } = usePriceExtractionsFetch()

    const [extractions, setExtractions] = useState<PriceExtraction[]>([])
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('')
    const [countryFilter, setCountryFilter] = useState<number | undefined>()
    const [projectFilter, setProjectFilter] = useState<number | undefined>()
    const [projectOptions, setProjectOptions] = useState<OptionSelectType[]>([])
    const [loadingProjects, setLoadingProjects] = useState(true)
    const [creatorFilter, setCreatorFilter] = useState<string | number | undefined>()
    const [creatorOptions, setCreatorOptions] = useState<OptionSelectType[]>([])
    const [loadingCreators, setLoadingCreators] = useState(true)
    const [initialized, setInitialized] = useState(false)

    const loadExtractions = useCallback(async (loadPage: number, status?: string, country?: number, project?: number, creator?: string | number) => {
        setLoading(true)
        const result = await getExtractions(loadPage, LIMIT, country, status, undefined, undefined, project, creator)
        setLoading(false)
        if (result.success) {
            setExtractions(result.data)
            setTotalPages(result.totalPages)
        } else {
            showAlertMessage('danger', result.message || 'Error al cargar extracciones')
        }
    }, [getExtractions, showAlertMessage])

    const loadProjects = useCallback(async () => {
        setLoadingProjects(true)
        const result = await getProjectsWithExtractions()
        if (result.success) {
            setProjectOptions(result.data.map(p => ({ value: p.id, label: p.title })))
        }
        setLoadingProjects(false)
    }, [getProjectsWithExtractions])

    const loadCreatorsList = useCallback(async () => {
        setLoadingCreators(true)
        const result = await getCreators()
        if (result.success) {
            setCreatorOptions(result.data.map(c => ({ value: c.id, label: c.name })))
        }
        setLoadingCreators(false)
    }, [getCreators])

    // Inicializar datos una sola vez
    if (!initialized) {
        setInitialized(true)
        loadExtractions(1)
        loadProjects()
        loadCreatorsList()
    }

    const handlePagination = (pageNav: number) => {
        if (pageNav !== page) {
            setPage(pageNav)
            loadExtractions(pageNav, statusFilter, countryFilter, projectFilter, creatorFilter)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleStatusChange = (status: string) => {
        setStatusFilter(status)
        setPage(1)
        loadExtractions(1, status, countryFilter, projectFilter, creatorFilter)
    }

    const handleCountryChange = (countryId: number | undefined) => {
        setCountryFilter(countryId)
        setPage(1)
        loadExtractions(1, statusFilter, countryId, projectFilter, creatorFilter)
    }

    const handleProjectChange = (option: OptionSelectType) => {
        const projectId = option.value || undefined
        setProjectFilter(projectId)
        setPage(1)
        loadExtractions(1, statusFilter, countryFilter, projectId, creatorFilter)
    }

    const handleCreatorChange = (option: OptionSelectType) => {
        const creatorId = option.value || undefined
        setCreatorFilter(creatorId)
        setPage(1)
        loadExtractions(1, statusFilter, countryFilter, projectFilter, creatorId)
    }

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getStatusBadge = (status: ExtractionStatus) => {
        return (
            <Badge bg={statusColors[status] || 'secondary'}>
                {statusLabels[status] || status}
            </Badge>
        )
    }

    return (
        <div className="p-5 admin-extractions-list">
            <div className="mb-4 py-2 px-4 d-flex justify-content-between flex-wrap position-relative">
                <Link to="/admin" className="fs-2 go-back mt-3 ms-3 ms-md-4">
                    <i className="text-light bi bi-arrow-left"></i>
                </Link>
                <h3>EXTRACCIONES DE PRECIOS</h3>
                <div className="d-flex gap-2">
                    <Link to="run-from-sheet">
                        <button className="btn btn-outline-primary">
                            <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                            DESDE SHEET
                        </button>
                    </Link>
                    <Link to="create">
                        <button className="btn btn-primary">NUEVA EXTRACCION</button>
                    </Link>
                </div>
            </div>

            {/* FILTROS */}
            <div className="d-flex mb-4 align-items-center flex-wrap gap-2">
                <DropdownButton
                    title={countryFilter ? COUNTRIES.find(c => c.id === countryFilter)?.name || 'Pais' : 'Todos los paises'}
                    variant="outline-secondary"
                >
                    <Dropdown.Item onClick={() => handleCountryChange(undefined)}>
                        Todos los paises
                    </Dropdown.Item>
                    {COUNTRIES.map(country => (
                        <Dropdown.Item key={country.id} onClick={() => handleCountryChange(country.id)}>
                            {country.name}
                        </Dropdown.Item>
                    ))}
                </DropdownButton>

                <DropdownButton
                    title={statusFilter ? statusLabels[statusFilter as ExtractionStatus] : 'Todos los estados'}
                    variant="outline-secondary"
                >
                    <Dropdown.Item onClick={() => handleStatusChange('')}>
                        Todos los estados
                    </Dropdown.Item>
                    {Object.entries(statusLabels).map(([key, label]) => (
                        <Dropdown.Item key={key} onClick={() => handleStatusChange(key)}>
                            {label}
                        </Dropdown.Item>
                    ))}
                </DropdownButton>

                <div style={{ minWidth: 250 }}>
                    <SpecialSelect
                        options={projectOptions}
                        selected={projectFilter ? projectOptions.find(p => p.value === projectFilter) || null : null}
                        onChange={handleProjectChange}
                        loading={loadingProjects}
                        placeholder="Filtrar por proyecto..."
                    />
                </div>

                <div style={{ minWidth: 200 }}>
                    <SpecialSelect
                        options={creatorOptions}
                        selected={creatorFilter ? creatorOptions.find(c => c.value === creatorFilter) || null : null}
                        onChange={handleCreatorChange}
                        loading={loadingCreators}
                        placeholder="Creado por..."
                    />
                </div>
            </div>

            {/* TABLA */}
            {!loading && extractions.length > 0 && (
                <Table bordered hover responsive>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Fecha</th>
                            <th>Pais</th>
                            <th>Proyectos</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>#Proy</th>
                            <th>Unidades</th>
                            <th>Cambios</th>
                            <th>Creado por</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {extractions.map(extraction => (
                            <tr key={extraction.id}>
                                <td>{extraction.id}</td>
                                <td>{formatDate(extraction.created_at)}</td>
                                <td>{extraction.country?.name || '-'}</td>
                                <td>
                                    <span
                                        title={extraction.projects?.map(p => p.title).join(', ') || '-'}
                                        style={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {extraction.projects?.map(p => p.title).join(', ') || '-'}
                                    </span>
                                </td>
                                <td>
                                    <Badge bg={extraction.extraction_type === 'pro' ? 'warning' : 'secondary'}>
                                        {extraction.extraction_type.toUpperCase()}
                                    </Badge>
                                </td>
                                <td>{getStatusBadge(extraction.status)}</td>
                                <td className="text-center">{extraction.total_projects}</td>
                                <td className="text-center">{extraction.total_units}</td>
                                <td className="text-center">
                                    {extraction.changes_pending > 0 && (
                                        <Badge bg="primary" className="me-1">{extraction.changes_pending} pend.</Badge>
                                    )}
                                    {extraction.changes_accepted > 0 && (
                                        <Badge bg="success" className="me-1">{extraction.changes_accepted} acept.</Badge>
                                    )}
                                    {extraction.changes_applied > 0 && (
                                        <Badge bg="info">{extraction.changes_applied} aplic.</Badge>
                                    )}
                                    {extraction.changes_pending === 0 && extraction.changes_accepted === 0 && extraction.changes_applied === 0 && '-'}
                                </td>
                                <td>{extraction.creator?.name || extraction.creator?.email || 'Automático'}</td>
                                <td>
                                    {(extraction.status === 'completed' || extraction.status === 'validated') && (
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => navigate(`validate/${extraction.id}`)}
                                        >
                                            Validar
                                        </button>
                                    )}
                                    {extraction.status === 'applied' && (
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => navigate(`validate/${extraction.id}`)}
                                        >
                                            Ver
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {loading && (
                <div style={{ height: 400 }} className="w-100 d-flex align-items-center justify-content-center">
                    <Lottie animationData={loaderIris} style={{ height: 100 }} loop />
                </div>
            )}

            {!loading && extractions.length === 0 && (
                <div className="text-center py-5">
                    <p className="text-muted">No hay extracciones registradas</p>
                    <Link to="create">
                        <button className="btn btn-primary">Crear primera extraccion</button>
                    </Link>
                </div>
            )}

            {extractions.length > 0 && totalPages > 1 && (
                <Pagination
                    currentPage={page}
                    handlePagination={handlePagination}
                    totalPages={totalPages}
                    maxPages={MAX_PAGES}
                />
            )}
        </div>
    )
}
